import { useState } from 'react'

import {
  NotificationChannel,
  Space,
  SpaceNotificationPreference,
} from '@tribeplatform/gql-client/types'
import { Form } from '@tribeplatform/react-sdk/components'
import {
  useSpaceDefaultNotificationSettings,
  useUpdateDefaultSpaceNotificationSettings,
} from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { NotificationPreferences } from '../../../Notification/NotificationPerferences'
import { NotificationPreferencesChannelOverride } from '../../../Notification/NotificationPreferencesChannelOverride'

export const SpaceSettingsNotifications = ({ space }: { space: Space }) => {
  const [inAppPreference, setInAppPreference] =
    useState<SpaceNotificationPreference>()
  const [emailPreference, setEmailPreference] =
    useState<SpaceNotificationPreference>()
  const [emailSameAsDefault, setEmailSameAtDefault] = useState<boolean>()

  const { data: spaceDefaultNotificationSettings } =
    useSpaceDefaultNotificationSettings({
      variables: {
        spaceId: space?.id,
      },
    })
  const { mutateAsync: updateDefaultSpaceNotificationSettings, isLoading } =
    useUpdateDefaultSpaceNotificationSettings()

  const inAppSettings = spaceDefaultNotificationSettings?.find(
    it => it.channel === NotificationChannel.IN_APP,
  )
  const emailSettings = spaceDefaultNotificationSettings?.find(
    it => it.channel === NotificationChannel.EMAIL,
  )

  return (
    <Form
      onSubmit={async () => {
        try {
          await updateDefaultSpaceNotificationSettings({
            spaceId: space?.id,
            input: {
              preference: inAppPreference,
            },
            channel: inAppSettings.channel,
          })
          await updateDefaultSpaceNotificationSettings({
            spaceId: space?.id,
            input: {
              preference: emailPreference,
              sameAsDefault: emailSameAsDefault,
            },
            channel: emailSettings.channel,
          })
          toast({
            title: 'Space default notification settings updated',
            status: 'success',
          })
        } catch (e) {
          toast({
            title: 'Unable to update space default notification settings',
            status: 'error',
          })
        }
      }}
    >
      <div className="col-span-12 md:col-span-8 flex flex-col space-y-5">
        <h2>Notifications</h2>
        <div>
          <FormControl.Label>Default notification settings</FormControl.Label>
          <FormControl.HelperText>
            Configure what the default notification setting should be when new
            users join this space.
          </FormControl.HelperText>
        </div>
        <NotificationPreferences
          value={inAppPreference ?? inAppSettings?.preference}
          onChange={preference => {
            setInAppPreference(preference)
          }}
          context="defaultSettings"
        />
        <NotificationPreferencesChannelOverride
          notificationSettings={{
            ...emailSettings,
            preference: emailPreference ?? emailSettings?.preference,
            sameAsDefault: emailSameAsDefault ?? emailSettings?.sameAsDefault,
          }}
          defaultValue={inAppSettings?.preference}
          onChange={(sameAsDefault, preference) => {
            setEmailPreference(preference)
            setEmailSameAtDefault(sameAsDefault)
          }}
        />
        <Form.Actions>
          <Button type="submit" loading={isLoading}>
            Update
          </Button>
        </Form.Actions>
      </div>
    </Form>
  )
}
